import { useEffect, useState } from 'react';
import type { VarianceSummary } from '@integrate/contracts';
import { describeScope, fmt, monthLabel, type SeparationState } from '@integrate/core';
import { api, ApiError } from '../api.js';

/*
  Plan versus what actually happened — the pane the product could not draw.

  WHY IT COULD NOT. A roster refresh marks somebody a leaver and, in the same transaction, clears
  their mitigation plan: a departed person strands nothing, and attrition beats a plan. Correct, and
  it meant the plan and the actual were destroyed and created together — by the time a person HAD an
  actual, the plan they had was gone, with no inverse to recover it from. The comparison never
  failed for want of arithmetic. It failed because one side of it was deleted a line before the
  other was written. So the departure now writes down what it clears, and this reads that ledger.

  A FETCHING PANE, like `ComparePane` and for a sharper version of the same reason. The compare pane
  fetches because the store holds one design at a time; this one fetches because the ledger is not a
  function of the design at ALL. Nothing on screen can be re-derived into it — that is the point of
  it — so there is no client-side computation to prefer, and a stale read is the only failure mode
  worth designing against. Re-fetched on mount and on demand, the same three states, the same
  caption naming whose numbers these are.

  NO CONTROLS, per the modal's standing rule: "A pane is never a command. Nothing here takes
  `onDispatch`." A departure is not a thing anybody edits from an analytics tab — it is a fact that
  arrived with a roster.
*/
export function VariancePane({
  state,
  projectId,
  scenarioId,
  scenarioName,
}: {
  /**
   * The month labels, the reader's scope, and — through the Day-1 date — a fetch dependency.
   *
   * Every number in the table is the server's, computed against the Day 1 the server held. Moving
   * Day 1 does not re-label those numbers, it INVALIDATES them: the month a departure fell in, the
   * variance against its plan, and the early/late tallies are all measured from the close. So this
   * pane re-reads when the date moves rather than relabelling what it has.
   */
  state: SeparationState;
  projectId: string;
  /**
   * Which design's plan these departures are measured against — a fetch dependency, not display.
   * The same person can be a rightsizing in one option and untouched in another, so switching
   * scenario behind this modal re-reads the ledger rather than re-labelling the old one.
   */
  scenarioId: string;
  scenarioName: string;
}): JSX.Element {
  const [variance, setVariance] = useState<VarianceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the retry and refresh buttons; the effect depends on it, so both are one re-fetch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setVariance(null);
    setError(null);
    api
      .variance(projectId, scenarioId)
      .then((response) => {
        if (!cancelled) setVariance(response.variance);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'The departure ledger could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
    /*
      THE CLOCK IS A DEPENDENCY, not a display input, and this line is the whole reason why. Every
      number in the payload is measured from Day 1 — the month a departure fell in, its variance
      against a plan, the early/late tallies — so moving Day 1 does not change how these rows should
      be LABELLED, it changes what they should SAY. An earlier draft depended on the project and the
      scenario only and re-labelled the indices it already had: shift Day 1 back six months and the
      pane confidently reported a departure as six months early that was by then exactly on plan,
      next to a calendar date that contradicted it.

      No race: `dispatch` in the store is deliberately not optimistic — "send it, wait, refetch" —
      so `state.assumptions.day1Date` only ever holds a date the server already has, and the read
      this fires cannot arrive before the ledger it is asking about.
    */
  }, [projectId, scenarioId, state.assumptions.day1Date, attempt]);

  if (error !== null) {
    return (
      <div data-testid="variance-pane">
        <p className="muted" data-testid="variance-error">
          {error}
        </p>
        <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
      </div>
    );
  }
  if (variance === null) {
    return (
      <p className="muted" data-testid="variance-loading">
        Reading the departure ledger…
      </p>
    );
  }

  /*
    THE SERVER'S VERDICT ON THE CALENDAR DECIDES THE LABELS, rather than this pane re-deriving it.

    The two are the same condition computed twice — the server asks whether the stored Day-1 date
    parses, `monthLabel` asks the same of the same string — and the refetch above keeps them looking
    at one date. Following the server's answer rather than re-deciding it is what makes that a fact
    about the code instead of a coincidence: a confident "Oct 26" beside a variance the server
    refused to compute would be two answers to the same question in one row, and this line is where
    that becomes impossible rather than merely unlikely.
  */
  const clock = variance.onACalendar ? state.assumptions : { day1Date: null };
  const month = (m: number): string => monthLabel(m, clock);

  /*
    WHOSE COUNTS THESE ARE, which is the one place this pane contradicts the shell around it.

    `Workbench` renders `ScopeNotice` unconditionally, and for a scoped reader it says "totals are
    for this part of the organisation" — true of every other pane, because every roll-up in this
    product is a pure function of `state.employees` and the scope narrows that array before anything
    is summed. This pane is the exception: the ledger is a stored artefact, served whole, narrowed
    by NAME at the door. So the counts below really are the project's, and a reader who took the
    notice at its word would read a company total as their slice's — the exact failure ScopeNotice
    exists to prevent, arriving through the one pane it cannot know about. The pane that breaks the
    promise is the pane that says so.

    Gated on `describeScope` because that is precisely the condition under which the notice makes
    the claim, and it names no denominator, for the reason the notice does not: "412 of 5,180" hands
    back the size of the population somebody may not see.
  */
  const scopeName = describeScope(state.scope?.filter ?? null);

  /*
    A NULL NAME HAS TWO CAUSES AND ONLY ONE OF THEM IS A PERMISSION. The server's map is the roster
    this reader's grant admitted, so a name goes missing either because the door withheld it or
    because the person is not in the roster at all — the ledger is append-only and survives a
    restore, so a departure can outlive the employee row a rollback removed. Telling an unrestricted
    owner that a name is "outside your scope" would be a confident wrong answer about their own
    access, so the sentence is chosen by whether this reader HAS a scope. The id stays hidden either
    way, which is the disclosure argument and is independent of this one.
  */
  const scoped = state.scope?.filter != null;
  const unnamed = scoped ? 'Outside your scope' : 'No longer in the roster';

  if (variance.rows.length === 0) {
    return (
      <div data-testid="variance-pane">
        <p className="muted" data-testid="variance-empty">
          Nobody has departed <strong>{scenarioName}</strong> since the ledger began. A departure is
          recorded when a roster refresh no longer carries somebody who was in the previous roster —
          so this fills in as the deal runs, and stays empty for a project still being designed.
        </p>
        {/*
          THE EMPTY STATE NEEDS THE DOOR MOST, which an earlier draft gave only to the table. This
          is the pane's ordinary resting state during a live deal, and it is the one that goes stale
          silently: nothing here re-reads on its own, and a refresh applied in another tab arrives as
          a departure this pane will not see until somebody switches tab and back. "Nobody has left"
          is exactly the claim worth being able to re-ask.
        */}
        <button data-testid="variance-refresh" onClick={() => setAttempt((n) => n + 1)}>
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div data-testid="variance-pane">
      <p className="muted small" style={{ marginTop: 0 }}>
        Everybody who has left <strong>{scenarioName}</strong> since the ledger began, newest first,
        beside the mitigation plan the refresh cleared. The plan is what it said at the moment they
        went; the design has forgotten it, deliberately, because a departed person strands nothing.
        The date is when a roster refresh first stopped carrying them, not the day they walked out —
        a roster loaded a month late records the departure a month late, and the tally leans late
        with it.
      </p>

      {/* See `scopeName` above: this pane's counts are the project's, where every other pane's are
          this reader's slice, and the notice in the frame says the opposite. */}
      {scopeName !== null && (
        <p className="muted small" data-testid="variance-wholeproject">
          These counts are the whole project's, not <strong>{scopeName}</strong>'s. A departure
          outside your access keeps its row and loses its name, so the totals below are true about
          the project rather than about your part of it.
        </p>
      )}

      <p data-testid="variance-summary">
        <strong>{fmt(variance.rows.length)}</strong>{' '}
        {variance.rows.length === 1 ? 'person has' : 'people have'} departed.{' '}
        {variance.planned === 0 ? (
          <>None carried a mitigation plan, so there is nothing here to measure against.</>
        ) : (
          <>
            <strong>{fmt(variance.planned)}</strong> carried a plan
            {variance.onACalendar ? (
              <>
                {' — '}
                <strong data-testid="variance-early">{fmt(variance.early)}</strong> left early and{' '}
                <strong data-testid="variance-late">{fmt(variance.late)}</strong> later than planned.
              </>
            ) : (
              '.'
            )}
          </>
        )}
      </p>

      {/*
        NO DAY 1, NO VARIANCE — said out loud rather than shown as an empty column.

        Every number in this product lives on a months-after-Day-1 axis and every departure happens
        on a calendar; without a Day-1 date there is nothing to carry one onto the other, and a
        variance measured against an unstated close would be a number with no meaning and a
        confident look. An empty column reads as a project where everything went to plan, which is
        the opposite of what it means here.
      */}
      {!variance.onACalendar && (
        <p className="muted small" data-testid="variance-nocalendar">
          This project has not said when Day 1 is, so a departure on the calendar and a plan in
          months cannot be compared. The plans below are still reported — it is the actual that has
          no axis to sit on. Set a Day-1 date in Assumptions and the variance appears.
        </p>
      )}

      <table className="data-table" data-testid="variance-table">
        <thead>
          <tr>
            <th>Person</th>
            {/*
              RECORDED, not "Left". The ledger stamps `now()` when the refresh is applied, so this
              is the moment the organisation noticed, not the moment the person went. Calling it
              "Left" would make every row a claim about an event nothing in this system observed —
              and the variance beside it inherits the same lag, which the caption says out loud.
            */}
            <th>Recorded</th>
            <th>Planned</th>
            <th className="num">Planned month</th>
            <th className="num">Actual month</th>
            <th className="num">Variance</th>
          </tr>
        </thead>
        <tbody data-testid="variance-rows" data-count={variance.rows.length}>
          {/*
            KEYED AND ADDRESSED BY POSITION, not by employee id, and that is a disclosure decision
            rather than a convenience. A scoped member is served every row so their counts stay
            true, with the names their grant does not admit nulled at the door — and a personnel id
            printed beside a departure date and a planned rightsizing would hand back a thin version
            of the name the door just took. The order is part of the contract (newest first), so
            position is stable within a read.
          */}
          {variance.rows.map((row, i) => (
            <tr key={i} data-testid={`variance-row-${i}`}>
              <td>
                {row.name === null ? (
                  <span className="muted" data-testid={`variance-anon-${i}`}>
                    {unnamed}
                  </span>
                ) : (
                  <>
                    <strong>{row.name}</strong>{' '}
                    <span className="muted small">{row.employeeId}</span>
                  </>
                )}
                {row.plannedDay2Exit && (
                  <span className="pill" data-testid={`variance-day2-${i}`}>
                    Day-2 exit
                  </span>
                )}
              </td>
              <td>
                {/*
                  PINNED TO UTC, against the house habit of bare `toLocaleDateString`, and the pin
                  is load-bearing rather than fussy. `monthOfDate` buckets the instant by the
                  YYYY-MM-DD prefix of its ISO string, which is UTC — so a departure stamped
                  2026-04-01T00:30Z read in Berlin would print "31.3.2026" beside an Actual month of
                  April. Two columns naming different months for one event is precisely the
                  contradiction this pane refuses everywhere else.
                */}
                {new Date(row.at).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                {/* The change number the departure landed at, so it can be found in History. */}
                <div className="muted small">change {fmt(row.seq)}</div>
              </td>
              <td>
                {row.plannedAction === null ? (
                  <span className="muted">no plan</span>
                ) : row.plannedAction === 'rightsize' ? (
                  'Rightsize'
                ) : (
                  'Redeploy'
                )}
              </td>
              <td className="num">{row.plannedMonth === null ? '—' : month(row.plannedMonth)}</td>
              {/*
                NULL IS NOT ZERO and must not be drawn as one: it means the question "how many
                months after Day 1" has no answer yet, not that the answer is "at the close".
              */}
              <td className="num">{row.actualMonth === null ? '—' : month(row.actualMonth)}</td>
              <td className="num" data-testid={`variance-delta-${i}`}>
                {row.varianceMonths === null ? (
                  '—'
                ) : row.varianceMonths === 0 ? (
                  <span className="muted">on plan</span>
                ) : (
                  `${fmt(Math.abs(row.varianceMonths))} ${
                    Math.abs(row.varianceMonths) === 1 ? 'month' : 'months'
                  } ${row.varianceMonths < 0 ? 'early' : 'late'}`
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <span className="muted small">
          A departure is append-only and survives a restore: the design can be rolled back, but they
          still left.
        </span>
        <span className="spacer" />
        <button data-testid="variance-refresh" onClick={() => setAttempt((n) => n + 1)}>
          Refresh
        </button>
      </div>
    </div>
  );
}
